import type { inferProcedureInput, inferProcedureOutput } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trpcClient } from '../../../utils/trpc'

type InitialData = inferProcedureOutput<AppRouter['admin']['cycleDashboard']['initialize']>
export type SearchQuery = inferProcedureInput<AppRouter['admin']['cycleDashboard']['search']>
export type CycleListItem = InitialData['cycles']['data'][number]
export interface AvailableActions {
	create: boolean
	duplicate: boolean
	delete: boolean
}
export interface DuplicateOptions {
	copyAdmins: boolean
	copyViewers: boolean
	copyParticipants: boolean
	copyCycleSteps: boolean
	copyCycleRoleMappings: boolean
	copyCycleReviewerGroups: boolean
	copyAsTemplate: boolean
}

// Store interface for injection
export interface CycleDashboardStore {
	// State from BFF
	cycles: InitialData['cycles']
	organization: InitialData['organization']
	actions: InitialData['actions']
	searchQuery: SearchQuery
	searchTimeout: NodeJS.Timeout

	// UI State
	isLoading: boolean
	error: string | null

	// Actions that map to BFF endpoints
	initialize: (organizationId: string) => Promise<void>
	setSearchQuery: (query?: Omit<SearchQuery, 'organizationId'>) => void
	search: () => Promise<void>
	selectedCycle: CycleListItem | null
	setSelectedCycle: (cycle: CycleListItem | null) => void
	duplicationOptions: DuplicateOptions
	setDuplicationOptions: (options?: Partial<DuplicateOptions>) => void
	duplicateSelectedCycle: () => Promise<string>
	deleteSelectedCycle: () => Promise<void>
	setError: (error?: string | null) => void
}

// Type for the store instance
export type CycleDashboardStoreInstance = ReturnType<typeof createCycleDashboardStore>

// Factory function for creating store instances
export const createCycleDashboardStore = () => {
	return create<CycleDashboardStore>()(
		devtools(
			(set, get) => ({
				// Initial state
				cycles: [],
				organization: {},
				meta: { page: 1, perPage: 20, total: 0, totalInPage: 0 },
				actions: [],
				isLoading: false,
				error: null,
				selectedCycle: null,
				duplicationOptions: {
					copyAdmins: true,
					copyViewers: true,
					copyParticipants: true,
					copyCycleSteps: true,
					copyCycleRoleMappings: true,
					copyCycleReviewerGroups: true,
					copyAsTemplate: false,
				},
				searchQuery: '',

				// Actions
				initialize: async (organizationId, params = {}) => {
					// Guard against re-initialization for the same org
					if (get().organization?.id === organizationId || get().isLoading) {
						return
					}
					set({ isLoading: true, error: null })
					try {
						// Call BFF init endpoint
						const data = await trpcClient.admin.cycleDashboard.initialize.query({
							organizationId,
							...params,
						})
						set({
							cycles: data.cycles,
							organization: data.organization,
							actions: data.actions,
							searchQuery: {
								organizationId,
							},
							isLoading: false,
						})
					} catch (error) {
						set({
							error: error instanceof Error ? error.message : 'Failed to load cycle dashboard',
							isLoading: false,
						})
					}
				},

				search: async () => {
					set({ error: null })
					const { organization, searchQuery } = get()
					if (!organization?.id) {
						// Skip search if organization is not yet initialized
						return
					}

					// Ensure organizationId is present in the query
					const queryWithOrg = {
						...searchQuery,
						organizationId: organization.id,
					}

					try {
						// Call BFF search endpoint
						const cycles = await trpcClient.admin.cycleDashboard.search.query(queryWithOrg)
						set({
							cycles,
						})
					} catch (error) {
						set({
							error: error instanceof Error ? error.message : 'Failed to search cycles',
						})
					}
				},

				duplicateSelectedCycle: async () => {
					const { organization, selectedCycle } = get()
					if (!organization) {
						throw new Error('No organization available')
					}
					if (!selectedCycle) {
						throw new Error('No cycle selected')
					}
					try {
						// Call BFF duplicate endpoint
						const newCycle = await trpcClient.admin.cycleDashboard.duplicate.mutate({
							organizationId: organization.id,
							cycleId: selectedCycle.id,
							name: `${selectedCycle.name} (Copy)`,
							...get().duplicationOptions,
						})

						// Refresh data after successful duplicate
						return newCycle.id
					} catch (error) {
						set({
							error: error instanceof Error ? error.message : 'Failed to duplicate cycle',
						})
						throw error
					} finally {
						await get().search()
						set({
							selectedCycle: null,
							duplicationOptions: {
								copyAdmins: true,
								copyViewers: true,
								copyParticipants: true,
								copyCycleSteps: true,
								copyCycleRoleMappings: true,
								copyCycleReviewerGroups: true,
								copyAsTemplate: false,
							},
						})
					}
				},

				deleteSelectedCycle: async () => {
					try {
						const { organization, selectedCycle } = get()
						if (!organization) {
							throw new Error('No organization available')
						}
						if (!selectedCycle) {
							throw new Error('No cycle selected')
						}

						await trpcClient.admin.cycleDashboard.delete.mutate({
							id: selectedCycle.id,
							organizationId: organization.id,
						})

						get().search()
					} catch (error) {
						console.error('Failed to delete cycle:', error)
						set({
							error: error instanceof Error ? error.message : 'Failed to delete cycle',
						})
					} finally {
						set({ selectedCycle: null })
					}
				},

				setSearchQuery: (query) => {
					const { searchQuery, searchTimeout, search } = get()

					if (searchTimeout) {
						clearTimeout(searchTimeout)
					}

					set({
						searchQuery: {
							...searchQuery,
							...query,
						},
						searchTimeout: setTimeout(search, 300),
					})
				},
				setSelectedCycle: (cycle: CycleListItem | null) => set({ selectedCycle: cycle }),
				setDuplicationOptions: (options) => {
					if (!options) {
						set({
							duplicationOptions: {
								copyAdmins: true,
								copyViewers: true,
								copyParticipants: true,
								copyCycleSteps: true,
								copyCycleRoleMappings: true,
								copyCycleReviewerGroups: true,
								copyAsTemplate: false,
							},
						})
						return
					}

					set((state) => ({
						duplicationOptions: {
							...state.duplicationOptions,
							...options,
						},
					}))
				},
				setError: (error) => set({ error }),
			}),
			{ name: 'cycle-dashboard-store' },
		),
	)
}
